// src/policy/canonicalRoles.ts
var CANONICAL_ROLES = [
  "PLATFORM_ADMIN",
  "OPS_ADMIN",
  "OPS_DISPATCHER",
  "OPS_VIEWER",
  "PLANNER",
  "APPROVER",
  "DISPATCHER",
  "SUPERVISOR",
  "DRIVER",
  "MAP_READ",
  "MAP_ADMIN",
  "INSPECTOR_ADMIN",
  "INSPECTOR_SUPERVISOR",
  "INSPECTOR_AGENT",
  "TICKETING_ADMIN",
  "TICKETING_SUPERVISOR",
  "TICKETING_OPERATOR",
  "TICKETING_AUDITOR",
  "SENTINEL_ADMIN",
  "SENTINEL_SUPERVISOR",
  "SENTINEL_DRIVER",
  "FLOW_ADMIN",
  "FLOW_DISPATCHER",
  "FLOW_SUPERVISOR",
  "PAYROLL_ADMIN",
  "PAYROLL_APPROVER",
  "PAYROLL_AUDITOR",
  "PAYROLL_VIEWER",
  "PIS_ADMIN",
  "PIS_OPERATOR",
  "PIS_READ",
  "PORTAL_ADMIN",
  "PORTAL_OPS",
  "PORTAL_DEV",
  "MANAGEMENT",
  "CONTROLLER",
  "ANALYST",
  // NEXT-3603: the gateway's own authority for approveDispatchAction / rejectDispatchAction
  // (DispatchActionLifecycleDataFetcher: DISPATCH_SUPERVISOR or ADMIN). Declared known so the
  // token normalizer no longer drops them before the OCC approve capability can be evaluated.
  "DISPATCH_SUPERVISOR",
  "ADMIN"
];
var CANONICAL_ROLE_SET = new Set(CANONICAL_ROLES);
function isCanonicalRole(role) {
  return CANONICAL_ROLE_SET.has(role);
}
function filterToCanonical(roles) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const r of roles) {
    if (CANONICAL_ROLE_SET.has(r) && !seen.has(r)) {
      seen.add(r);
      out.push(r);
    }
  }
  return out;
}

// src/policy/apps.ts
var COCKPIT_WEB = {
  appId: "cockpit-frontend",
  acceptedRoles: [
    "PLATFORM_ADMIN",
    "OPS_ADMIN",
    "OPS_DISPATCHER",
    "OPS_VIEWER",
    "PLANNER",
    "APPROVER",
    "DISPATCHER",
    "SUPERVISOR"
  ],
  capabilities: {
    "app.access": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "PLANNER",
      "APPROVER",
      "DISPATCHER",
      "SUPERVISOR"
    ],
    "scenario.view": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "PLANNER",
      "APPROVER",
      "DISPATCHER",
      "SUPERVISOR"
    ],
    "scenario.create": ["PLATFORM_ADMIN", "OPS_ADMIN", "PLANNER"],
    "scenario.update": ["PLATFORM_ADMIN", "OPS_ADMIN", "PLANNER"],
    "scenario.delete": ["PLATFORM_ADMIN", "OPS_ADMIN"],
    "scenario.approve": ["PLATFORM_ADMIN", "OPS_ADMIN", "APPROVER"],
    "scenario.export": ["PLATFORM_ADMIN", "OPS_ADMIN", "APPROVER"],
    "dispatch.view": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "DISPATCHER",
      "SUPERVISOR"
    ],
    "dispatch.command": ["PLATFORM_ADMIN", "OPS_ADMIN", "OPS_DISPATCHER", "DISPATCHER"],
    "map.view": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "DISPATCHER",
      "SUPERVISOR"
    ]
  }
};
var OCC_CLIENT = {
  appId: "occ-frontend",
  acceptedRoles: [
    "PLATFORM_ADMIN",
    "OPS_ADMIN",
    "OPS_DISPATCHER",
    "OPS_VIEWER",
    "PLANNER",
    "MAP_READ",
    "MAP_ADMIN",
    "DISPATCHER",
    "SUPERVISOR",
    "DISPATCH_SUPERVISOR",
    "ADMIN"
  ],
  capabilities: {
    "app.access": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "PLANNER",
      "MAP_READ",
      "MAP_ADMIN",
      "DISPATCHER",
      "SUPERVISOR"
    ],
    // NEXT-3644 (governor ruling 2026-09-26): the OCC dispatcher operates from /map, Horario and
    // Marey, so OPS_DISPATCHER holds map.read. Read only: map.admin is not granted.
    "map.read": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "PLANNER",
      "MAP_READ",
      "MAP_ADMIN"
    ],
    "map.admin": ["PLATFORM_ADMIN", "OPS_ADMIN", "MAP_ADMIN"],
    "occ.dispatch.view": [
      "PLATFORM_ADMIN",
      "OPS_ADMIN",
      "OPS_DISPATCHER",
      "OPS_VIEWER",
      "DISPATCHER",
      "SUPERVISOR"
    ],
    "occ.dispatch.command": ["PLATFORM_ADMIN", "OPS_ADMIN", "OPS_DISPATCHER", "DISPATCHER"],
    // NEXT-3603: approve / reject a dispatch action. Mirrors the gateway exactly
    // (approveDispatchAction / rejectDispatchAction: DISPATCH_SUPERVISOR or ADMIN); the server
    // remains the enforcer. A plain DISPATCHER or SUPERVISOR does not hold it.
    "occ.dispatch.approve": ["DISPATCH_SUPERVISOR", "ADMIN"],
    // NEXT-3794: register / heartbeat / end an operator session. Mirrors the governed grant of
    // the authority `occ.session.write` exactly (authority-model.json: ADMIN, MAP_ADMIN,
    // OPS_ADMIN, OPS_DISPATCHER; DISPATCHER_SUPERUSER is not an OCC client role). The server
    // remains the enforcer; this only stops the client initiating a call it will be refused.
    "occ.session.write": ["ADMIN", "MAP_ADMIN", "OPS_ADMIN", "OPS_DISPATCHER"],
    // NEXT-3794 option 1b: this is the existing mutation capability, not route admission.
    // Read personas retain map.read / occ.dispatch.view; neither implies this edit grant.
    // Mutation controls also require the gateway's exact, current leaf decision.
    "occ.operations.edit": ["OPS_DISPATCHER", "MAP_ADMIN", "DISPATCHER", "ADMIN"]
  }
};
var INSPECTOR_CLIENT = {
  appId: "inspector-frontend",
  acceptedRoles: ["PLATFORM_ADMIN", "INSPECTOR_ADMIN", "INSPECTOR_SUPERVISOR", "INSPECTOR_AGENT"],
  capabilities: {
    "app.access": [
      "PLATFORM_ADMIN",
      "INSPECTOR_ADMIN",
      "INSPECTOR_SUPERVISOR",
      "INSPECTOR_AGENT"
    ],
    "inspector.view": [
      "PLATFORM_ADMIN",
      "INSPECTOR_ADMIN",
      "INSPECTOR_SUPERVISOR",
      "INSPECTOR_AGENT"
    ],
    "inspector.write": ["PLATFORM_ADMIN", "INSPECTOR_ADMIN", "INSPECTOR_SUPERVISOR"],
    "inspector.admin": ["PLATFORM_ADMIN", "INSPECTOR_ADMIN"]
  }
};
var TICKETING_CLIENT = {
  appId: "ticketing-frontend",
  acceptedRoles: [
    "PLATFORM_ADMIN",
    "TICKETING_ADMIN",
    "TICKETING_SUPERVISOR",
    "TICKETING_OPERATOR",
    "TICKETING_AUDITOR"
  ],
  capabilities: {
    "app.access": [
      "PLATFORM_ADMIN",
      "TICKETING_ADMIN",
      "TICKETING_SUPERVISOR",
      "TICKETING_OPERATOR",
      "TICKETING_AUDITOR"
    ],
    "ticketing.view": [
      "PLATFORM_ADMIN",
      "TICKETING_ADMIN",
      "TICKETING_SUPERVISOR",
      "TICKETING_OPERATOR",
      "TICKETING_AUDITOR"
    ],
    "ticketing.write": [
      "PLATFORM_ADMIN",
      "TICKETING_ADMIN",
      "TICKETING_SUPERVISOR",
      "TICKETING_OPERATOR"
    ],
    "ticketing.audit": ["PLATFORM_ADMIN", "TICKETING_ADMIN", "TICKETING_AUDITOR"],
    "ticketing.admin": ["PLATFORM_ADMIN", "TICKETING_ADMIN"]
  }
};
var SENTINEL_WEB = {
  appId: "sentinel-frontend",
  acceptedRoles: ["PLATFORM_ADMIN", "SENTINEL_ADMIN", "SENTINEL_SUPERVISOR"],
  capabilities: {
    "app.access": ["PLATFORM_ADMIN", "SENTINEL_ADMIN", "SENTINEL_SUPERVISOR"],
    "sentinel.view": ["PLATFORM_ADMIN", "SENTINEL_ADMIN", "SENTINEL_SUPERVISOR"],
    "sentinel.admin": ["PLATFORM_ADMIN", "SENTINEL_ADMIN"]
  }
};
var SENTINEL_DRIVER = {
  appId: "sentinel-driver-frontend",
  // Driver app explicitly does NOT accept PLATFORM_ADMIN — admins who need
  // driver functions get a SENTINEL_DRIVER role assigned to their account.
  acceptedRoles: ["SENTINEL_DRIVER"],
  capabilities: {
    "app.access": ["SENTINEL_DRIVER"],
    "driver.shift.view": ["SENTINEL_DRIVER"],
    "driver.shift.update": ["SENTINEL_DRIVER"]
  }
};
var FLOW = {
  appId: "flow-frontend",
  acceptedRoles: ["PLATFORM_ADMIN", "FLOW_ADMIN", "FLOW_DISPATCHER", "FLOW_SUPERVISOR"],
  capabilities: {
    "app.access": ["PLATFORM_ADMIN", "FLOW_ADMIN", "FLOW_DISPATCHER", "FLOW_SUPERVISOR"],
    "flow.view": ["PLATFORM_ADMIN", "FLOW_ADMIN", "FLOW_DISPATCHER", "FLOW_SUPERVISOR"],
    "flow.dispatch": ["PLATFORM_ADMIN", "FLOW_ADMIN", "FLOW_DISPATCHER"],
    "flow.admin": ["PLATFORM_ADMIN", "FLOW_ADMIN"]
  }
};
var PAYROLL_WEB = {
  appId: "payroll-frontend",
  acceptedRoles: [
    "PLATFORM_ADMIN",
    "PAYROLL_ADMIN",
    "PAYROLL_APPROVER",
    "PAYROLL_AUDITOR",
    "PAYROLL_VIEWER"
  ],
  capabilities: {
    "app.access": [
      "PLATFORM_ADMIN",
      "PAYROLL_ADMIN",
      "PAYROLL_APPROVER",
      "PAYROLL_AUDITOR",
      "PAYROLL_VIEWER"
    ],
    "payroll.view": [
      "PLATFORM_ADMIN",
      "PAYROLL_ADMIN",
      "PAYROLL_APPROVER",
      "PAYROLL_AUDITOR",
      "PAYROLL_VIEWER"
    ],
    "payroll.approve": ["PLATFORM_ADMIN", "PAYROLL_ADMIN", "PAYROLL_APPROVER"],
    "payroll.audit": ["PLATFORM_ADMIN", "PAYROLL_ADMIN", "PAYROLL_AUDITOR"],
    "payroll.admin": ["PLATFORM_ADMIN", "PAYROLL_ADMIN"]
  }
};
var PIS_CLIENT = {
  appId: "pis-frontend",
  acceptedRoles: ["PLATFORM_ADMIN", "PIS_ADMIN", "PIS_OPERATOR", "PIS_READ"],
  capabilities: {
    "app.access": ["PLATFORM_ADMIN", "PIS_ADMIN", "PIS_OPERATOR", "PIS_READ"],
    "pis.view": ["PLATFORM_ADMIN", "PIS_ADMIN", "PIS_OPERATOR", "PIS_READ"],
    "pis.write": ["PLATFORM_ADMIN", "PIS_ADMIN", "PIS_OPERATOR"],
    "pis.admin": ["PLATFORM_ADMIN", "PIS_ADMIN"]
  }
};
var PULSE_WEB = {
  appId: "pulse-frontend",
  acceptedRoles: ["PLATFORM_ADMIN", "MANAGEMENT", "CONTROLLER", "ANALYST"],
  capabilities: {
    "app.access": ["PLATFORM_ADMIN", "MANAGEMENT", "CONTROLLER", "ANALYST"],
    "pulse.view": ["PLATFORM_ADMIN", "MANAGEMENT", "CONTROLLER", "ANALYST"],
    "pulse.controller": ["PLATFORM_ADMIN", "MANAGEMENT", "CONTROLLER"],
    "pulse.admin": ["PLATFORM_ADMIN", "MANAGEMENT"]
  }
};
var SMARTTT_PORTAL = {
  appId: "smarttt-portal",
  acceptedRoles: ["PLATFORM_ADMIN", "PORTAL_ADMIN", "PORTAL_OPS", "PORTAL_DEV"],
  capabilities: {
    "app.access": ["PLATFORM_ADMIN", "PORTAL_ADMIN", "PORTAL_OPS", "PORTAL_DEV"],
    "portal.view": ["PLATFORM_ADMIN", "PORTAL_ADMIN", "PORTAL_OPS", "PORTAL_DEV"],
    "portal.admin": ["PLATFORM_ADMIN", "PORTAL_ADMIN"]
  }
};
var APP_POLICIES = Object.freeze([
  COCKPIT_WEB,
  OCC_CLIENT,
  INSPECTOR_CLIENT,
  TICKETING_CLIENT,
  SENTINEL_WEB,
  SENTINEL_DRIVER,
  FLOW,
  PAYROLL_WEB,
  PIS_CLIENT,
  PULSE_WEB,
  SMARTTT_PORTAL
]);

// src/policy/registry.ts
var PolicyValidationError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PolicyValidationError";
  }
};
function validatePolicy(policy) {
  if (!policy.appId || policy.appId.length === 0) {
    throw new PolicyValidationError("AppAuthorizationPolicy.appId is required.");
  }
  for (const r of policy.acceptedRoles) {
    if (!isCanonicalRole(r)) {
      throw new PolicyValidationError(
        `[${policy.appId}] acceptedRoles contains unknown role "${r}". Add it to CANONICAL_ROLES first.`
      );
    }
  }
  const accepted = new Set(policy.acceptedRoles);
  for (const [cap, roles] of Object.entries(policy.capabilities)) {
    if (!roles || roles.length === 0) {
      throw new PolicyValidationError(
        `[${policy.appId}] capability "${cap}" has no roles. Empty role lists deny by default \u2014 remove the entry instead.`
      );
    }
    for (const r of roles) {
      if (!isCanonicalRole(r)) {
        throw new PolicyValidationError(
          `[${policy.appId}] capability "${cap}" references unknown role "${r}".`
        );
      }
      if (!accepted.has(r)) {
        throw new PolicyValidationError(
          `[${policy.appId}] capability "${cap}" references "${r}" which is not in acceptedRoles. Add it to acceptedRoles or remove from this capability.`
        );
      }
    }
  }
}
var DefaultPolicyRegistry = class {
  byApp = /* @__PURE__ */ new Map();
  constructor(initial) {
    for (const p of initial) {
      validatePolicy(p);
      if (this.byApp.has(p.appId)) {
        throw new PolicyValidationError(
          `Duplicate AppAuthorizationPolicy for appId "${p.appId}".`
        );
      }
      this.byApp.set(p.appId, p);
    }
  }
  get(appId) {
    return this.byApp.get(appId);
  }
  list() {
    return Array.from(this.byApp.values());
  }
};
function createPolicyRegistry(policies) {
  return new DefaultPolicyRegistry(policies);
}
var defaultPolicyRegistry = new DefaultPolicyRegistry(APP_POLICIES);

// src/policy/evaluator.ts
function lookup(appId, registry) {
  return registry.get(appId);
}
function getEffectiveRoles(appId, user, registry = defaultPolicyRegistry) {
  if (!user) return [];
  const policy = lookup(appId, registry);
  if (!policy) return [];
  const userCanonical = filterToCanonical(user.allRoles);
  if (userCanonical.length === 0) return [];
  const accepted = new Set(policy.acceptedRoles);
  return userCanonical.filter((r) => accepted.has(r));
}
function hasRole(appId, role, user, registry = defaultPolicyRegistry) {
  if (!user) return false;
  const policy = lookup(appId, registry);
  if (!policy) return false;
  if (!policy.acceptedRoles.includes(role)) return false;
  return user.allRoles.includes(role);
}
function hasCapability(appId, capability, user, registry = defaultPolicyRegistry) {
  if (!user) return false;
  const policy = lookup(appId, registry);
  if (!policy) return false;
  const allowed = policy.capabilities[capability];
  if (!allowed || allowed.length === 0) return false;
  const accepted = new Set(policy.acceptedRoles);
  for (const role of user.allRoles) {
    if (!accepted.has(role)) continue;
    if (allowed.includes(role)) return true;
  }
  return false;
}
function getCapabilities(appId, user, registry = defaultPolicyRegistry) {
  if (!user) return [];
  const policy = lookup(appId, registry);
  if (!policy) return [];
  const accepted = new Set(policy.acceptedRoles);
  const userAccepted = user.allRoles.filter((r) => accepted.has(r));
  if (userAccepted.length === 0) return [];
  const granted = [];
  for (const [cap, roles] of Object.entries(policy.capabilities)) {
    if (userAccepted.some((r) => roles.includes(r))) {
      granted.push(cap);
    }
  }
  return granted;
}
function getEffectiveAuthorization(appId, user, registry = defaultPolicyRegistry) {
  return {
    appId,
    effectiveRoles: getEffectiveRoles(appId, user, registry),
    capabilities: getCapabilities(appId, user, registry)
  };
}
function canEnterApp(appId, user, registry = defaultPolicyRegistry) {
  return hasCapability(appId, "app.access", user, registry);
}

export {
  CANONICAL_ROLES,
  isCanonicalRole,
  filterToCanonical,
  APP_POLICIES,
  PolicyValidationError,
  createPolicyRegistry,
  defaultPolicyRegistry,
  getEffectiveRoles,
  hasRole,
  hasCapability,
  getCapabilities,
  getEffectiveAuthorization,
  canEnterApp
};
//# sourceMappingURL=chunk-U523KRWQ.js.map